import React, { useState } from "react";

import { Box, Collapse, Typography } from "@material-ui/core";
import { CloudUploadRounded } from "@material-ui/icons";
import { ReactComponent as CloseIconSvg } from "@remar/shared/dist/assets/icons/icon-close-circle.svg";
import { FilesUploaded } from "@remar/shared/dist/components/FilesUploaded/FilesUploaded";
import Uploader from "@remar/shared/dist/components/Uploader";
import { useDispatch } from "react-redux";
import { Subject } from "rxjs";
import { _emit, emit } from "store/features/notifications/notifications.slice";
import { genericService } from "store/services";

import theme from "theme/default";

import { CustomInput as StyledInput, useStyles } from "./styles";

import { Header } from "../../ManageStudents/styles";

export default ({
	removeQuestionAttachment,
	removeQuestionRationalVideo,
	setStateValue,
	uploadQuestionMaterial,
	uploadRationalVideo,
	validateForm,
	attachments,
	rationaleVideo,
	description,
	children,
	endOfQuestionSummary,
	mainImageKey,
	statePath = "",
	lessonId = 0,
	questionId = "",
	expanded = true,
	mediaAfterChildren = false,
	hideDescription = false
}) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const defaultCustomInputOptions = { _emit, dispatch, setStateValue, validateForm };

	const [error, setError] = useState(false);
	const [uploadingProgress, setUploadingProgress] = useState(0);
	const [rationaleVideoUploadProgress, setRationalVideoUploadProgress] = useState(0);
	const [attachmentLoadingType, setAttachmentLoadingType] = useState<"attachments" | "video" | null>(null);

	const fullStatePath = statePath ? `inputs.${statePath}` : "inputs";

	const handleUpload = async (file: Partial<File>): Promise<void> => {
		const methods = {
			clearFileInput: () => clearFileInputSubject.next(),
			progress: setUploadingProgress,
			uploadError: setError
		};

		setAttachmentLoadingType("attachments");

		dispatch(
			uploadQuestionMaterial({
				file,
				fullStatePath,
				...(lessonId && { lessonId }),
				...(questionId && { questionId }),
				options: {
					onError: () => {
						setAttachmentLoadingType(null);
						methods.uploadError(true);
					},
					onProgress: ({ loaded, total }) => {
						methods.progress((loaded / total) * 100);
					},
					onUploaded: () => {
						setAttachmentLoadingType(null);
						methods.clearFileInput();
					}
				}
			})
		);

		methods.uploadError(false);
	};
	const clearFileInputSubject = new Subject<void>();

	const handleRationaleVideoUpload = async (file: Partial<File>): Promise<void> => {
		const methods = {
			clearFileInput: () => clearRationalFileInputSubject.next(),
			progress: setRationalVideoUploadProgress,
			uploadError: setError
		};

		setAttachmentLoadingType("video");

		dispatch(
			uploadRationalVideo({
				file,
				fullStatePath,
				...(lessonId && { lessonId }),
				...(questionId && { questionId }),
				options: {
					onError: () => {
						setAttachmentLoadingType(null);
						methods.uploadError(true);
					},
					onProgress: ({ loaded, total }) => {
						methods.progress((loaded / total) * 100);
					},
					onUploaded: () => {
						setAttachmentLoadingType(null);
						methods.clearFileInput();
					}
				}
			})
		);

		methods.uploadError(false);
	};
	const clearRationalFileInputSubject = new Subject<void>();

	const UploadMediaContent = () => (
		<Box display="flex">
			<Box mt={3} flex="0 0 50%">
				<Typography className={classes.addOptionHeader}>UPLOAD MATERIALS</Typography>
				<Box display="flex" mt={3}>
					<Uploader
						accept=".pdf,.jpeg,.jpe,.jpg,.ppt,.pptx"
						clearFileInputSubject={clearFileInputSubject}
						error={error}
						header="Upload Media"
						icon={<CloudUploadRounded name="cloud-upload" style={{ width: "50px", height: "50px" }} />}
						loading={attachmentLoadingType === "attachments"}
						onAdd={files => handleUpload(files[0])}
						progress={uploadingProgress}
						dragFileError="File Type is not allowed."
						title="Drag and Drop File Here or Browse to Choose a File. PPT, JPEG, PDF types are allowed."
						emit={emit}
					/>
				</Box>
			</Box>
			<Box width="45%" ml="5%" mt={3}>
				{attachments?.length > 0 && (
					<Box>
						<Typography className={classes.addOptionHeader}>FILES UPLOADED</Typography>
						<FilesUploaded
							showTitle={false}
							attachments={attachments.map(attachment => ({
								fileName: attachment.fileName.value,
								name: attachment.name.value,
								fileSizeInBytes: attachment.fileSizeInBytes.value,
								fileType: attachment.fileType.value,
								lessonId: 0,
								id: 0
							}))}
							IconSvg={CloseIconSvg}
							onClick={attachment =>
								dispatch(
									removeQuestionAttachment({
										fullStatePath,
										index: attachments.findIndex(({ fileName }) => fileName.value === attachment.fileName)
									})
								)
							}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);

	const UploadRationalMediaContent = () => (
		<Box display="flex">
			<Box mt={3} flex="0 0 50%">
				<Typography className={classes.addOptionHeader}>Upload RATIONALE PHOTO or video</Typography>
				<Box display="flex" mt={3}>
					<Uploader
						accept=".mp4,.mov"
						disabled={!!rationaleVideo?.id.value}
						clearFileInputSubject={clearRationalFileInputSubject}
						error={error}
						header="Upload Rational Video"
						icon={<CloudUploadRounded name="cloud-upload" style={{ width: "50px", height: "50px" }} />}
						loading={attachmentLoadingType === "video"}
						onAdd={files => handleRationaleVideoUpload(files[0])}
						progress={rationaleVideoUploadProgress}
						dragFileError="File Type is not allowed."
						title="Drag and Drop File Here or Browse to Choose a File. MP4, MOV types are allowed."
						emit={emit}
					/>
				</Box>
			</Box>
			<Box width="45%" ml="5%" mt={3}>
				{!!rationaleVideo?.id.value && (
					<Box>
						<Typography className={classes.addOptionHeader}>FILES UPLOADED</Typography>
						<FilesUploaded
							showTitle={false}
							attachments={[rationaleVideo].map(r => ({
								fileName: r.fileName.value,
								name: r.name.value,
								fileSizeInBytes: r.fileSizeInBytes.value,
								totalLengthInSeconds: r.totalLengthInSeconds.value,
								fileType: r.fileType.value,
								lessonId: 0,
								id: r.id.value
							}))}
							IconSvg={CloseIconSvg}
							onClick={() => dispatch(removeQuestionRationalVideo({ fullStatePath }))}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);

	return (
		<Collapse in={expanded} unmountOnExit>
			{!hideDescription && (
				<>
					<Header>Question Description</Header>
					<StyledInput mr={2} theme={theme} options={{ ...defaultCustomInputOptions, inputData: description }} />{" "}
				</>
			)}

			{!lessonId && <UploadRationalMediaContent />}
			{!mediaAfterChildren && attachments && <UploadMediaContent />}
			{children}
			{mediaAfterChildren && attachments && <UploadMediaContent />}

			<div className={classes.rationalContainer}>
				<Typography className={classes.rationalText}>Rationale</Typography>
				<StyledInput
					mr={2}
					theme={theme}
					options={{
						...defaultCustomInputOptions,
						inputData: endOfQuestionSummary
					}}
				/>
				<Box display="flex" mt={3}>
					<StyledInput
						width={246}
						height={246}
						theme={theme}
						inputProps={{
							fileUploaderDescription: (
								<>
									Drag and Drop File Here or Browse to Choose a File <br />
									<br />
									Recommended Size 720x500
								</>
							)
						}}
						options={{
							...defaultCustomInputOptions,
							genericApiService: genericService,
							inputData: mainImageKey
						}}
					/>
				</Box>
			</div>
		</Collapse>
	);
};
